import { createContext, useContext, useState } from "react";

import { ConsultationTypes } from "~/common/utils";
import { SupplementalQuestion } from "~/providers/types/supplementalQuestions";
import { AddSupplementalQuestionModal } from "./AddSupplementalQuestionModal";
import { DeleteSupplementalQuestionModal } from "./DeleteSupplementalQuestionModal";
import { EditSupplementalQuestionModal } from "./EditSupplementalQuestionModal";
import { ReorderSupplementalQuestionsModal } from "./ReorderSupplementalQuestionsModal";
import { SupplementalQuestionsTable } from "./SupplementalQuestionsTable";

interface SupplementalQuestionsEditorProps {
  type: ConsultationTypes;
  questions: SupplementalQuestion[];
}

interface SupplementalQuestionsEditorContext {
  type: ConsultationTypes;
  questions: SupplementalQuestion[];
}

const SupplementalQuestionsEditorContext =
  createContext<SupplementalQuestionsEditorContext | null>(null);

export const useSupplementalQuestionsEditorContext = () => {
  const editor = useContext(SupplementalQuestionsEditorContext);
  if (!editor)
    throw Error(
      "useSupplementalQuestionsEditorContext can only be used inside a SupplementalQuestionsEditorContext.Provider",
    );
  return editor;
};

export const SupplementalQuestionsEditor = ({
  type,
  questions,
}: SupplementalQuestionsEditorProps) => {
  const [showReorder, setShowReorder] = useState(false);
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [showEditQuestion, setShowEditQuestion] = useState(false);
  const [showDeleteQuestion, setShowDeleteQuestion] = useState(false);
  const [questionToEdit, setQuestionToEdit] =
    useState<SupplementalQuestion | null>(null);
  const [questionToDelete, setQuestionToDelete] =
    useState<SupplementalQuestion | null>(null);

  return (
    <SupplementalQuestionsEditorContext.Provider value={{ type, questions }}>
      <div>
        <AddSupplementalQuestionModal
          show={showAddQuestion}
          onClose={() => setShowAddQuestion(false)}
        />

        <>
          <ReorderSupplementalQuestionsModal
            show={showReorder}
            onClose={() => setShowReorder(false)}
          />
          {questionToEdit && (
            <EditSupplementalQuestionModal
              key={questionToEdit.id}
              initialData={questionToEdit}
              show={showEditQuestion}
              onClose={() => setShowEditQuestion(false)}
            />
          )}
          {questionToDelete && (
            <DeleteSupplementalQuestionModal
              show={showDeleteQuestion}
              onClose={() => setShowDeleteQuestion(false)}
              questionId={questionToDelete.id}
            />
          )}

          <SupplementalQuestionsTable
            onReorder={() => setShowReorder(true)}
            onAddQuestion={() => setShowAddQuestion(true)}
            onEditQuestion={(question) => {
              setQuestionToEdit(question);
              setShowEditQuestion(true);
            }}
            onDeleteQuestion={(question) => {
              setQuestionToDelete(question);
              setShowDeleteQuestion(true);
            }}
          />
        </>
      </div>
    </SupplementalQuestionsEditorContext.Provider>
  );
};
